/** Semantic checks for the existing EMRG 1.0 wire format. */
export function wireErrors(bytes: Uint8Array, tier: 0 | 1 | 2): string[] {
  const errors: string[] = [];
  const check = (valid: boolean, message: string) => { if (!valid) errors.push(message); };
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const category = bytes[tier === 0 ? 2 : 26]!;
  check(category >= 1 && category <= 15, 'Invalid event category');
  if (tier > 0) check(bytes[6] === category, 'Header and event category differ');
  const priority = bytes[tier === 0 ? 8 : 13]! >>> 4;
  check(priority <= 5, 'Invalid priority');
  const flags = bytes[tier === 0 ? 9 : 17]!;
  check((flags & 15) === 0, 'Reserved location flags');
  const latitude = view.getInt32(tier === 0 ? 10 : 18, false);
  const longitude = view.getInt32(tier === 0 ? 14 : 22, false);
  check(flags & 0x80 ? Math.abs(latitude) <= 90000000 && Math.abs(longitude) <= 180000000 : latitude === 0x7fffffff && longitude === 0x7fffffff, 'Invalid GPS coordinates or unavailable sentinels');
  const severity = tier === 0 ? bytes[18]! >>> 4 : bytes[27]!;
  const status = tier === 0 ? bytes[18]! & 15 : bytes[28]!;
  const radius = tier === 0 ? bytes[19]! >>> 4 : bytes[29]!;
  check(severity >= 1 && severity <= 5, 'Invalid severity');
  check(status >= 1 && status <= 8, 'Invalid event status');
  check(radius <= 7, 'Invalid radius');
  if (tier === 0) check((bytes[19]! & 15) === 0, 'Reserved radius bits');
  check((view.getUint16(tier === 0 ? 20 : 30, false) & 3) === 0, 'Reserved response flags');
  if (tier > 0) {
    const slots = tier === 1 ? 5 : 10;
    const count = bytes[32]!;
    check(count <= slots, 'Resource count exceeds tier capacity');
    for (let index = 0; index < slots; index++) {
      const type = bytes[33 + index * 2]!;
      const state = bytes[34 + index * 2]!;
      check(index < count ? type >= 1 && type <= 10 && state <= 5 : type === 0 && state === 0, 'Invalid resource code or unused-slot padding');
    }
  }
  const encoding = tier === 0 ? 1 : bytes[tier === 1 ? 43 : 53]!;
  const field = bytes.subarray(tier === 0 ? 24 : tier === 1 ? 46 : 56, bytes.length - 2);
  try { validateTextField(field, encoding); } catch (error) { errors.push((error as Error).message); }
  return errors;
}

export function validateTextField(field: Uint8Array, encoding: number): void {
  if (encoding === 0 || encoding === 3) {
    if (field.some(byte => byte !== 0)) throw new Error('No-text encoding has nonzero text bytes');
    return;
  }
  if (encoding === 2) {
    let terminated = false;
    let accumulator = 0;
    let available = 0;
    for (const byte of field) {
      accumulator |= byte << available;
      available += 8;
      while (available >= 7) {
        const character = accumulator & 127;
        if (character === 0) terminated = true;
        else if (terminated || character < 0x20 || character > 0x7e) throw new Error('Invalid packed text or padding');
        accumulator >>>= 7;
        available -= 7;
      }
    }
    if (accumulator !== 0) throw new Error('Nonzero packed text padding bits');
    return;
  }
  if (encoding !== 1 && encoding !== 4) throw new Error('Unknown text encoding');
  const end = field.indexOf(0);
  const text = end < 0 ? field : field.subarray(0, end);
  if (end >= 0 && field.subarray(end).some(byte => byte !== 0)) throw new Error('Nonzero text padding');
  if (encoding === 1 && text.some(byte => byte < 0x20 || byte > 0x7e)) throw new Error('Invalid printable ASCII text');
  if (encoding === 4) new TextDecoder('utf-8', { fatal: true, ignoreBOM: true }).decode(text);
}
